import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup } from '@angular/forms';
import {
  IIrembogovBasicLabelKeyPair,
  ToastService,
} from '@irembo-andela/irembogov3-common';
import { ServiceChangeRequestService } from '../../../../core/services/service-change-request.service';
import { EntityWithSubscriptions } from '../../../../core/classes/entity-with-subscriptions';
import { IServiceLanguage } from '../../../../core/models/service-language.model';

enum EReminderTypes {
  Email = 'Email',
  SMS = 'SMS',
}

@Component({
  selector: 'irembogov-configure-reminders',
  templateUrl: './configure-reminders.component.html',
  styleUrls: ['./configure-reminders.component.scss'],
})
export class ConfigureRemindersComponent
  extends EntityWithSubscriptions
  implements OnInit
{
  configureRemindersForm!: FormGroup;
  workFlow: any[] = [];
  data: any = {};
  loading = false;
  changeRequestId!: string | null;
  languages!: IServiceLanguage[];

  quillConfig = {
    toolbar: [['bold', 'italic', 'underline'], ['link']],
  };

  constructor(
    private formBuilder: FormBuilder,
    private toastService: ToastService,
    private serviceChangeRequestService: ServiceChangeRequestService
  ) {
    super();
  }

  ngOnInit(): void {
    this.changeRequestId =
      this.serviceChangeRequestService.requestId.getValue();
    this.subscriptions.push(
      this.serviceChangeRequestService.languages.subscribe(response => {
        this.languages = response.filter(
          language => language.status === 'ACTIVE'
        );
        this.initForm();
        this.loadNotificationConfiguration();
      })
    );
  }

  private initForm(): void {
    const languageGroups: any = {};

    // Initialize form groups for each language
    this.languages.forEach(language => {
      languageGroups[language.locale] = this.formBuilder.group({
        subject: [''],
        emailBody: [''],
        smsBody: [''],
      });
    });

    // Create the main form
    this.configureRemindersForm = this.formBuilder.group({
      reminderOffsetDays: [''],
      languageTemplate: this.formBuilder.group(languageGroups),
    });
  }

  notificationConfigurationTabs: IIrembogovBasicLabelKeyPair<EReminderTypes>[] =
    [
      {
        label: 'Email',
        key: EReminderTypes.Email,
        icon: 'fa-solid fa-envelope',
      },
      {
        label: 'SMS',
        key: EReminderTypes.SMS,
        icon: 'fas fa-mobile',
      },
    ];

  selectedNotificationConfigurationTab: EReminderTypes = EReminderTypes.Email;
  protected readonly EReminderTypes = EReminderTypes;

  loadNotificationConfiguration() {
    if (!this.changeRequestId) return;

    this.subscriptions.push(
      this.serviceChangeRequestService
        .getServiceChangeRequestWorkflowDefinitionAndNextStepsAndProcessingTime(
          this.changeRequestId
        )
        .subscribe({
          next: response => {
            const lastWorkflowItem = (response as any)?.data?.['workflow']?.[
              (response as any).data['workflow'].length - 1
            ];
            this.data = response.data;
            this.workFlow = (response as any)?.data['workflow'];

            const reminderOffsetDays =
              lastWorkflowItem?.endStateOne?.breakingActions?.[0]?.args
                ?.reminderOffsetDays;

            // Create patch value object
            const patchValue: any = {
              reminderOffsetDays: reminderOffsetDays || 7,
              languageTemplate: {},
            };

            // Handle template data for each language
            this.languages.forEach(language => {
              const languageTemplate =
                lastWorkflowItem?.endStateOne?.breakingActions?.[0]?.args
                  ?.reminderNotifications?.staticTemplate?.[language.locale];

              patchValue.languageTemplate[language.locale] = {
                subject: languageTemplate?.subject || '',
                emailBody: languageTemplate?.emailBody || '',
                smsBody: languageTemplate?.smsBody || '',
              };
            });

            // Patch the form with the values
            this.configureRemindersForm.patchValue(patchValue);
          },
          error: error => {
            this.toastService.show({
              body: error.message || 'Failed to fetch reminder config.',
              type: 'error',
            });
          },
        })
    );
  }

  saveReminderConfig() {
    this.loading = true;
    const formValue = this.configureRemindersForm.value;
    const reminderOffsetDays = formValue.reminderOffsetDays;
    const languageTemplate = formValue.languageTemplate;

    const workflow = this.workFlow;
    if (workflow && workflow.length > 0) {
      const lastItem = { ...workflow[workflow.length - 1] };
      try {
        lastItem.endStateOne = {
          ...lastItem.endStateOne,
          breakingActions: [
            {
              ...lastItem.endStateOne?.breakingActions[0],
              args: {
                ...lastItem.endStateOne?.breakingActions[0]?.args,
                reminderOffsetDays: reminderOffsetDays,
                reminderNotifications: {
                  ...lastItem.endStateOne?.breakingActions[0]?.args
                    ?.reminderNotifications,
                  staticTemplate: {
                    ...lastItem.endStateOne?.breakingActions[0]?.args
                      ?.reminderNotifications?.staticTemplate,
                    ...languageTemplate,
                  },
                },
              },
            },
          ],
        };
        workflow.push(lastItem); // Add the updated item to the array
        this.workFlow = workflow;
      } catch (error) {
        this.toastService.show({
          body:
            'An error occurred while updating the workflow: ' +
            ((error as Error)?.message || 'Unknown error'),
          type: 'error',
        });
        this.loading = false;
        return;
      }
      if (!this.changeRequestId) {
        this.toastService.show({
          body: 'Change request id is not defined.',
          type: 'error',
        });
        this.loading = false;
        return;
      }
      this.serviceChangeRequestService
        .updateServiceChangeRequestWorkflowDefinitionAndNextStepsAndProcessingTime(
          this.changeRequestId,
          {
            workflow: this.workFlow,
            nextSteps: this.data.nextSteps,
            expectedProcessingTime: this.data.expectedProcessingTime,
            expectedProcessingMeasurementUnit:
              this.data.expectedProcessingMeasurementUnit,
          }
        )
        .subscribe({
          next: () => {
            this.toastService.show({
              body: 'Reminder configuration updated successfully.',
              type: 'success',
            });
            this.loadNotificationConfiguration();
            this.loading = false;
          },
          error: error => {
            this.toastService.show({
              body: error.message || 'Failed to update reminder config.',
              type: 'error',
            });
            this.loadNotificationConfiguration();
            this.loading = false;
          },
        });
    } else {
      this.loading = false;
      this.toastService.show({
        body: 'Workflow array is empty or undefined.',
        type: 'error',
      });
    }
  }
}
